import { ENVIRONMENT, isDevelopment } from '@app/environment';
import { InputErrorTemplate } from '@core/errors/error-template';
import { AppErrors } from '@core/errors/errors';
import {
  EmailValidator,
  FirebaseAuthValidator,
  NetworkErrorValidator,
  PasswordValidator,
} from '@core/errors/generic-errors';
import { PrefetchService } from '@core/prefetch/prefetch.service';
import { GithubService } from '@core/services/github/github.service';
import { Inject } from '@rxdi/core';
import { Form, FormGroup } from '@rxdi/forms';
import { Component, css, html, property } from '@rxdi/lit-html';
import { Router } from '@rxdi/router';
import { InputStyle } from '@rxdi/ui-kit/styles/form/input';
import { BaseComponentWithLoading } from '@shared/loading/loading.component';
import { SocialService } from '@stradivario/graphql-server';
import { switchMap } from 'rxjs/operators';

import { RegisterFormModel } from './signin.model';

/**
 * @customElement signin-component
 */
@Component({
  selector: 'signin-component',
  style: css`
    ${InputStyle}

    .input-has-error {
      background-color: white;
    }
    textarea,
    select,
    input,
    button {
      outline: none;
    }
    input[type='text'],
    input[type='email'],
    input[type='number'],
    textarea,
    fieldset {
      /* required to properly style form
   elements on WebKit based browsers */
      -webkit-appearance: none;

      width: 100%;
      margin: 0;

      font-family: inherit;
      font-size: 90%;

      -moz-box-sizing: border-box;
      box-sizing: border-box;
    }

    .is-invalid {
      box-shadow: 0 0 1px 1px #c95454;
    }

    .container {
      padding: 50px;
      margin: 0px auto;
      margin-top: 150px;
      width: 440px;
      background-color: #f8f8f8;
      border-radius: 15px;
      box-shadow: rgba(162, 162, 162, 0.25) 0px 0px 3px, rgba(0, 0, 0, 0.22) 0px 0px 3px;
    }
    h1,
    p {
      color: black;
    }
    p {
      color: black;
      padding: 0;
      margin: 0;
    }
    input[type='password']:disabled {
      background: #a9a9a9;
    }
    input[type='password']:disabled::placeholder {
      color: white;
    }
  `,
  template(this: SignUpComponent) {
    return html`
      ${this.getLoadingTemplate()}
      <div class="container">
        <div class="form-group card card-container">
          <div style="text-align: center;">
            <p style="font-weight: 400;font-size: 2em;margin-bottom: 20px;">Get started with Graphql Cloud</p>
          </div>
          <div
            @mouseenter=${() => this.prefetch.fetch(import('../projects/projects.module'))}
            style="margin: 0 auto; width: 220px;"
          >
            <social-login-container>Signup with GitHub</social-login-container>
          </div>
          <form
            name="register-form"
            class="form-signin ${!this.form.valid ? 'has-error' : ''}"
            novalidate
            @submit=${() => !this.hasErrors() && this.onSubmit()}
          >
            <p>Email</p>
            <input name="email" type="email" value=${this.model.email} class="rx-input" placeholder="Email address" />
            <div style="height: 20px;">${InputErrorTemplate(this.form.get('email'))}</div>

            <p style="margin-top: 10px;">Password</p>
            <input
              type="password"
              value=${this.model.password}
              name="password"
              class="rx-input"
              placeholder="Password"
            />
            <div style="height: 20px;">${InputErrorTemplate(this.form.get('password'))}</div>

            <div style="height:20px"></div>
            <rx-button style="width:100%" palette="primary" type="submit"> Register </rx-button>
            <rx-button
              style="width:100%; margin-top: 10px;"
              @click=${async () => {
                const email = this.form.getValue('email') as never;
                if (!email) {
                  this.form.get('password').disabled = true;
                  return await this.form.updateValueAndValidity();
                }
                const returnUrl = isDevelopment()
                  ? `http://localhost:1234/auth/${email}`
                  : `https://${ENVIRONMENT.DOMAIN_NAME}/auth/${email}`;

                this.socialService.sendSignInLinkToEmail(email, returnUrl).subscribe(
                  () => {
                    this.router.go(`/signup/success/${email}`);
                  },
                  (e) => console.error(e)
                );
              }}
              >Send Magic link
            </rx-button>
          </form>
        </div>
      </div>

      <div style="text-align: center; margin-bottom: 30px; margin-top: 10px;">
        Already have an account?<a style="cursor: pointer;color: #359ec0" @click=${() => this.router.go('/login')}>
          Log in</a
        >
      </div>
    `;
  },
})
export class SignUpComponent extends BaseComponentWithLoading {
  @property({ type: Object })
  error: unknown;

  @Router()
  private router: Router;

  @Inject(SocialService)
  private socialService: SocialService;

  @Inject(GithubService)
  private githubService: GithubService;

  @Inject(PrefetchService)
  private prefetch: PrefetchService;

  @property({ type: Object })
  private model = new RegisterFormModel();

  @Form({
    strategy: 'input',
    name: 'register-form',
    strict: true,
  })
  private form = new FormGroup(
    {
      password: [this.model.password, [PasswordValidator]],
      email: [this.model.email, [NetworkErrorValidator, EmailValidator, FirebaseAuthValidator]],
    },
    AppErrors
  );

  onSubmit() {
    if (this.isLoading()) {
      return;
    }
    this.showLoading();
    this.socialService
      .createUserWithEmailAndPassword(this.form.value.email as never, this.form.value.password as never)
      .pipe(
        switchMap((data) =>
          this.mutate({
            mutation: 'create-user-mutation.graphql',
            variables: {
              githubAccessToken: this.githubService.getAccessToken(),
              payload: { photoURL: data.user.photoURL },
            },
          })
        )
      )
      .subscribe(
        () => {
          this.hideLoading();
          this.router.go('/projects');
        },
        async (e) => {
          this.hideLoading();
          this.error = e;
          await this.form.updateValueAndValidity();
          this.error = '';
        }
      );
  }

  async hasErrors() {
    return (await this.form.updateValueAndValidity()).length;
  }
}
